import { memo, useCallback, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Alert, Box, Typography } from '@mui/material';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { FormContainer } from 'components/layout';
import { ChipInput, GroupedSelectableGrid } from 'components/common';
import datadogActions from 'lib/datadog';
import { useLocationParams } from 'hooks';
import { formatEntityStatus, toIdSelectionEntries } from 'utils';
import usePinterestAdGroupsData from '../hooks/usePinterestAdGroupsData';
import type { PinterestGridRow } from '../types';
import { getPinterestGridRowId, isPinterestAdGroupRow } from '../types';

export const UNPARSEABLE_FLOW_TOOLTIP = 'Flow is missing or the ad group name is incorrectly structured';
export const NO_SELECTABLE_AD_GROUPS_HINT = 'No ad groups with a defined flow';

const cellText = (value: string, hint?: string) => (
  <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', gap: 1 }}>
    <Typography variant="body2">{value}</Typography>
    {hint ? (
      <Typography variant="caption" color="text.secondary">
        — {hint}
      </Typography>
    ) : null}
  </Box>
);

const hasSelectableAdGroups = (row: PinterestGridRow) =>
  isPinterestAdGroupRow(row) || row.ad_groups.some((adGroup) => !!adGroup.flow);

const COLUMNS: GridColDef<PinterestGridRow>[] = [
  {
    field: 'name',
    headerName: 'Name',
    flex: 1,
    minWidth: 400,
    sortable: false,
    renderCell: (params: GridRenderCellParams<PinterestGridRow>) =>
      isPinterestAdGroupRow(params.row)
        ? cellText(params.row.ad_group_name)
        : cellText(params.row.campaign_name, hasSelectableAdGroups(params.row) ? undefined : NO_SELECTABLE_AD_GROUPS_HINT),
  },
  {
    field: 'flow',
    headerName: 'Flow',
    width: 240,
    sortable: false,
    renderCell: (params: GridRenderCellParams<PinterestGridRow>) =>
      isPinterestAdGroupRow(params.row) ? cellText(params.row.flow ?? '—') : null,
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 110,
    sortable: false,
    renderCell: (params: GridRenderCellParams<PinterestGridRow>) =>
      isPinterestAdGroupRow(params.row) ? cellText(formatEntityStatus(params.row.status)) : null,
  },
];

// Ads need the flow for the destination URL, so an ad group without a parsed flow cannot be picked.
const isSelectableRow = (row: PinterestGridRow) => isPinterestAdGroupRow(row) && !!row.flow;
const getRowTooltip = (row: PinterestGridRow) =>
  isPinterestAdGroupRow(row) && !row.flow ? UNPARSEABLE_FLOW_TOOLTIP : undefined;

const ChooseAdGroups = memo(() => {
  const {
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext();
  const adGroupsError = errors.selectedAdGroups?.message as string | undefined;

  const { gridRows, selectedAdGroups, isLoading, isError, errorMessage } = usePinterestAdGroupsData();
  const filterByName = useWatch({ name: 'filterAdGroupsByName' }) as string[] | undefined;
  const { setSelectedAdSets: setSelectedAdSetsParam, setFilterAdSetsByName: setFilterAdSetsByNameParam } =
    useLocationParams();

  useEffect(() => {
    setSelectedAdSetsParam(toIdSelectionEntries(selectedAdGroups));
  }, [selectedAdGroups, setSelectedAdSetsParam]);

  useEffect(() => {
    setFilterAdSetsByNameParam(filterByName ?? []);
  }, [filterByName, setFilterAdSetsByNameParam]);

  const handleSelectionChange = useCallback(
    (ids: string[]) => {
      setValue('selectedAdGroups', ids, { shouldValidate: true, shouldDirty: true });
    },
    [setValue],
  );

  useEffect(() => {
    void trigger('selectedAdGroups');
  }, [trigger]);

  useEffect(() => {
    if (!isError) return;

    datadogActions.trackDatadogError(new Error(errorMessage ?? 'Failed to load ad groups'), {
      source: 'pinterest_choose_ad_groups_query',
    });
  }, [isError, errorMessage]);

  return (
    <FormContainer
      title="Choose Ad Groups"
      error={adGroupsError}
      sx={{ flexDirection: 'column', alignItems: 'flex-start', gap: 2, height: '100%' }}
    >
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', width: '100%' }}>
        <Box sx={{ flex: 1, maxWidth: '50%' }}>
          <ChipInput name="filterAdGroupsByName" inputLabel="Enter ad group name" />
        </Box>
      </Box>

      {isError ? (
        <Alert severity="error" sx={{ width: '100%' }} data-testid="pinterest-ad-groups-error">
          {errorMessage}
        </Alert>
      ) : (
        <GroupedSelectableGrid
          rows={gridRows}
          columns={COLUMNS}
          isChildRow={isPinterestAdGroupRow}
          getRowId={getPinterestGridRowId}
          selectedIds={selectedAdGroups}
          onSelectionChange={handleSelectionChange}
          isRowSelectable={isSelectableRow}
          getRowTooltip={getRowTooltip}
          isLoading={isLoading}
          footerMessage={adGroupsError}
        />
      )}
    </FormContainer>
  );
});

ChooseAdGroups.displayName = 'ChooseAdGroups';

export default ChooseAdGroups;
